// Part 8 — micro-interactions for the BUYER surfaces only.
//
// Two channels, both optional and both silent when they should be:
//   * a short synthesised click (WebAudio — no asset to download, no request)
//   * a haptic tick where the device has one (Android; iOS Safari has no API)
//
// Rules that keep it from being obnoxious:
//   * muted until the buyer has interacted with the page at least once
//     (browsers refuse audio before a gesture anyway, and a surprise noise is
//     worse than no noise)
//   * honours `prefers-reduced-motion`, which is the closest standard signal
//     for "stop doing extra things at me"
//   * one persisted opt-out (`yot_tactile`), remembered per device
//   * never throws: an unavailable AudioContext just means no sound

const KEY = 'yot_tactile';

let ctx = null;
let armed = false;           // a real user gesture has happened on this page
// The DEALERSHIP's default, pushed in by whichever buyer screen loaded the
// vehicle. Off here means silent for every buyer of that dealership, whatever
// their own device says; on preserves the per-device opt-out below.
let orgAllows = true;

/** Set from the public vehicle payload (`action_noise`). */
export const setOrgTactile = (allowed) => { orgAllows = allowed !== false; };

const reducedMotion = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch { return false; }
};

export const tactileEnabled = () => {
    if (!orgAllows) return false;
    try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; }
};

/** True only when the dealership allows sounds at all — the buyer's own toggle
 *  is pointless to show when the whole dealership is silent. */
export const tactileAllowedByOrg = () => orgAllows;

export const setTactileEnabled = (on) => {
    try { localStorage.setItem(KEY, on ? 'on' : 'off'); } catch { /* private mode */ }
};

const audio = () => {
    if (ctx) return ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try { ctx = new AC(); } catch { ctx = null; }
    return ctx;
};

// A click, not a beep: a very short blip with a fast exponential decay.
const blip = (freq, ms, gain) => {
    const ac = audio();
    if (!ac) return;
    if (ac.state === 'suspended') { try { ac.resume(); } catch { /* ignore */ } }
    const t = ac.currentTime;
    const osc = ac.createOscillator();
    const amp = ac.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + ms / 1000);
    osc.connect(amp).connect(ac.destination);
    osc.start(t);
    osc.stop(t + ms / 1000 + 0.02);
};

const buzz = (pattern) => {
    try { navigator.vibrate?.(pattern); } catch { /* not supported */ }
};

const VARIANTS = {
    // [frequency, duration ms, gain, vibration pattern]
    tap: [520, 45, 0.05, 12],
    select: [660, 55, 0.05, 14],
    success: [720, 90, 0.07, [14, 40, 22]],
    error: [220, 130, 0.07, [30, 50, 30]],
};

export function feedback(variant = 'tap') {
    if (!tactileEnabled()) return;
    const [freq, ms, gain, vib] = VARIANTS[variant] || VARIANTS.tap;
    buzz(vib);
    if (reducedMotion() || !armed) return;
    blip(freq, ms, gain);
}

/** Arm + fire from one delegated listener on a buyer screen's root element.
 *  Returns the cleanup function, so it is a one-liner inside useEffect. */
export function attachTactile(root) {
    if (!root) return () => {};
    const onDown = (e) => {
        const el = e.target?.closest?.('button, [role="button"], a[href], label, summary');
        if (!el || el.disabled) return;
        const variant = el.getAttribute('data-tactile') || 'tap';
        feedback(variant);
        armed = true;                 // the gesture that unlocks audio
    };
    root.addEventListener('pointerdown', onDown, { passive: true });
    return () => root.removeEventListener('pointerdown', onDown);
}
